import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Loader2, Upload, Plus, Trash2, GripVertical, Image as ImageIcon,
  Video as VideoIcon, Users, MessageSquare, Megaphone, Settings, Calendar, Eye, EyeOff, Monitor,
  Flag, FileUp, Mail, Clock, Sparkles, Film, Star, Pencil, Check, X, Braces,
} from "lucide-react";
import * as tus from "tus-js-client";

// Mirror of backend EMAIL_VARIABLES (server.py). Substituted per-registrant at send time.
const EMAIL_VARS = [
  { token: "{{FIRST_NAME}}", label: "First name" },
  { token: "{{FULL_NAME}}", label: "Full name" },
  { token: "{{EMAIL}}", label: "Email address" },
  { token: "{{EXPERIENCE_TITLE}}", label: "Experience title" },
  { token: "{{SESSION_TIME}}", label: "Session date & time" },
  { token: "{{JOIN_URL}}", label: "Join link (watch room)" },
  { token: "{{HOST_NAME}}", label: "Host name" },
];

function HtmlEditor({ value, onChange, rows = 6, testid, label = "HTML content" }) {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);
  const insert = (token) => {
    const el = ref.current;
    const v = value || "";
    const start = el ? el.selectionStart : v.length;
    const end = el ? el.selectionEnd : v.length;
    const next = v.slice(0, start) + token + v.slice(end);
    onChange(next);
    setOpen(false);
    requestAnimationFrame(() => {
      if (el) { el.focus(); const pos = start + token.length; el.setSelectionRange(pos, pos); }
    });
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-[10px] uppercase tracking-wider font-bold text-[#9CA3AF]">{label}</label>
        <div className="relative">
          <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`${testid}-insert-var`}
            className="text-xs font-semibold text-[#6D28B5] hover:text-[#4A1A7A] flex items-center gap-1 border border-[#E5E0F5] px-2 py-1 rounded">
            <Braces className="w-3.5 h-3.5" /> Insert variable
          </button>
          {open && (
            <>
              <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
              <div className="absolute right-0 mt-1 z-30 w-60 bg-white border border-[#0A0A0A] shadow-xl max-h-64 overflow-y-auto" data-testid={`${testid}-var-menu`}>
                {EMAIL_VARS.map((v) => (
                  <button key={v.token} type="button" onClick={() => insert(v.token)}
                    data-testid={`${testid}-var-${v.token.replace(/[{}]/g, "")}`}
                    className="w-full text-left px-3 py-2 hover:bg-[#F1EEFB] border-b border-[#F3F4F6] last:border-0">
                    <div className="text-sm font-medium text-[#0A0A0A]">{v.label}</div>
                    <div className="text-[11px] text-[#9CA3AF] font-mono">{v.token}</div>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
      <textarea ref={ref} value={value} onChange={(e) => onChange(e.target.value)} rows={rows}
        className={`${input} font-mono text-xs`} data-testid={testid} />
    </div>
  );
}

const TABS = [
  { key: "details", label: "Details", icon: Settings },
  { key: "media", label: "Media", icon: ImageIcon },
  { key: "schedule", label: "Schedule", icon: Calendar },
  { key: "hosts", label: "Hosts", icon: Users },
  { key: "chat", label: "Chat script", icon: MessageSquare },
  { key: "ctas", label: "CTAs", icon: Megaphone },
  { key: "emails", label: "Emails", icon: Mail },
  { key: "reviews", label: "Reviews", icon: Star },
  { key: "endscreen", label: "End screen", icon: Flag },
];

const newHost = () => ({ id: crypto.randomUUID(), name: "", title: "", bio: "", avatar_url: "", on_screen: true });
const newMsg = () => ({ id: crypto.randomUUID(), time_seconds: 60, author: "", avatar_url: "", message: "" });
const newCta = () => ({ id: crypto.randomUUID(), time_seconds: 300, duration_seconds: 0, headline: "", description: "", button_text: "Buy now", button_url: "" });

function timeToSec(str) {
  const parts = str.split(":").map((n) => parseInt(n) || 0);
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  return parseInt(str) || 0;
}
function secToTime(s) {
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs uppercase tracking-wider font-bold text-[#4B5563] mb-1.5 block">{label}</label>
    {children}
  </div>
);
const input = "w-full border border-[#0A0A0A] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#6D28B5]";

export default function WebinarEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [w, setW] = useState(null);
  const [meProfile, setMeProfile] = useState(null);
  useEffect(() => { api.get("/auth/me").then((r) => setMeProfile(r.data)).catch(() => {}); }, []);
  const [tab, setTab] = useState("details");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/admin/webinars/${id}`).then((r) => setW(r.data)).catch(() => navigate("/admin/webinars"));
  }, [id, navigate]);

  const set = (patch) => setW((prev) => ({ ...prev, ...patch }));
  const reload = () => api.get(`/admin/webinars/${id}`).then((r) => setW(r.data)).catch(() => {});

  const save = async () => {
    const guestMissingName = (w.hosts || []).some((h) => !h.user_id && !(h.name || "").trim());
    if (guestMissingName) {
      toast.error("Please give every host a name before saving.");
      return;
    }
    setSaving(true);
    try {
      const res = await api.put(`/admin/webinars/${id}`, w);
      setW(res.data);
      toast.success("Saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    }
    setSaving(false);
  };

  if (!w)
    return (
      <div className="p-8 flex items-center gap-2 text-[#9CA3AF]">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading…
      </div>
    );

  return (
    <div className="pb-20" data-testid="webinar-editor">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white border-b border-[#E5E7EB] px-8 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4 min-w-0">
          <Link to="/admin/webinars" className="p-2 hover:bg-[#F3F4F6] shrink-0">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <div className="min-w-0">
            <input
              value={w.title}
              onChange={(e) => set({ title: e.target.value })}
              data-testid="editor-title-input"
              className="font-heading font-black text-xl tracking-tighter bg-transparent focus:outline-none w-full"
            />
            <div className="text-xs text-[#9CA3AF] font-mono">/{w.slug}</div>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer" data-testid="publish-toggle" title="When on, people can find and register for this experience. When off, it stays a private draft only you can see.">
            <input type="checkbox" checked={!!w.published} onChange={(e) => set({ published: e.target.checked })} className="w-4 h-4 accent-[#6D28B5]" />
            Make this live
            <span className={`text-xs font-bold px-2 py-0.5 ${w.published ? "bg-[#D1FAE5] text-[#065F46]" : "bg-[#F3F4F6] text-[#9CA3AF]"}`}>{w.published ? "LIVE" : "DRAFT"}</span>
          </label>
          {w.published && (
            <a href={`/w/${w.slug}`} target="_blank" rel="noopener noreferrer" className="border border-[#0A0A0A] hover:bg-[#F3F4F6] px-3 py-2 text-sm font-semibold flex items-center gap-1.5">
              <Eye className="w-4 h-4" /> Preview
            </a>
          )}
          <Link to={`/admin/webinars/${id}/registrants`} className="border border-[#0A0A0A] hover:bg-[#F3F4F6] px-3 py-2 text-sm font-semibold flex items-center gap-1.5" data-testid="editor-registrants-btn">
            <Users className="w-4 h-4" /> Registrants
          </Link>
          <button onClick={save} disabled={saving} data-testid="save-webinar-btn" className="bg-[#6D28B5] text-white hover:bg-[#4A1A7A] disabled:opacity-50 px-5 py-2 font-semibold flex items-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="px-8 border-b border-[#E5E7EB] flex gap-1 bg-white overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`tab-${t.key}`}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
              tab === t.key ? "border-[#6D28B5] text-[#6D28B5]" : "border-transparent text-[#4B5563] hover:text-[#0A0A0A]"
            }`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      <div className="p-8 max-w-4xl">
        {tab === "details" && <DetailsTab w={w} set={set} isAdmin={isAdmin} />}
        {tab === "media" && <MediaTab w={w} set={set} />}
        {tab === "schedule" && <ScheduleTab w={w} set={set} />}
        {tab === "hosts" && <HostsTab w={w} set={set} me={meProfile || user} />}
        {tab === "chat" && <ChatTab w={w} set={set} reload={reload} />}
        {tab === "ctas" && <CtasTab w={w} set={set} />}
        {tab === "emails" && <SequenceTab w={w} />}
        {tab === "reviews" && <ReviewsTab w={w} isAdmin={isAdmin} />}
        {tab === "endscreen" && <EndScreenTab w={w} set={set} />}
      </div>
    </div>
  );
}

function DetailsTab({ w, set, isAdmin }) {
  return (
    <div className="space-y-6 max-w-2xl">
      <Field label="Title">
        <input value={w.title} onChange={(e) => set({ title: e.target.value })} className={input} data-testid="details-title" />
      </Field>
      <Field label="URL slug">
        <div className="flex items-center gap-2">
          <span className="text-sm text-[#9CA3AF] font-mono shrink-0">/w/</span>
          <input
            value={w.slug || ""}
            onChange={(e) => set({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, "-") })}
            className={`${input} font-mono`}
            placeholder="my-experience-name"
            data-testid="details-slug"
          />
        </div>
        <p className="text-xs text-[#9CA3AF] mt-1.5">This is the web address people use to register. Leave blank to auto-generate from the title. Must be unique, if it's taken we'll add a short suffix.</p>
      </Field>
      <Field label="Description">
        <textarea value={w.description} onChange={(e) => set({ description: e.target.value })} rows={4} className={input} data-testid="details-description" />
      </Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Experience feel">
          <select value={w.mode} onChange={(e) => set({ mode: e.target.value })} className={input} data-testid="details-mode">
            <option value="live">Live-style (simulated live chat)</option>
            <option value="evergreen">On-demand (quieter)</option>
          </select>
        </Field>
        <Field label="Duration (minutes)">
          <input type="number" value={Math.round(w.duration_seconds / 60)} onChange={(e) => set({ duration_seconds: (parseInt(e.target.value) || 0) * 60 })} className={input} data-testid="details-duration" />
        </Field>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Price (0 = free)">
          <input type="number" step="0.01" value={w.price} onChange={(e) => set({ price: parseFloat(e.target.value) || 0 })} className={input} data-testid="details-price" />
        </Field>
        <Field label="Currency">
          <select value={w.currency} onChange={(e) => set({ currency: e.target.value })} className={input} data-testid="details-currency">
            <option value="usd">USD</option>
            <option value="eur">EUR</option>
            <option value="gbp">GBP</option>
            <option value="mxn">MXN</option>
          </select>
        </Field>
      </div>
      <p className="text-xs text-[#6B7280] -mt-2">Both feels are evergreen and run on your schedule. "Live-style" adds the simulated live chat; "On-demand" keeps it calm.</p>

      {isAdmin && (
        <div className="border-t border-[#E5E7EB] pt-5">
          <div className="text-xs uppercase tracking-wider font-bold text-[#6D28B5] mb-3">Email sender for this experience</div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="From email (optional)">
              <input value={w.sender_email || ""} onChange={(e) => set({ sender_email: e.target.value })} placeholder="happiness@yourdomain.com" className={input} data-testid="details-sender-email" />
            </Field>
            <Field label="From name (optional)">
              <input value={w.sender_name || ""} onChange={(e) => set({ sender_name: e.target.value })} placeholder="Upward Cast" className={input} data-testid="details-sender-name" />
            </Field>
          </div>
          <p className="text-xs text-[#6B7280] mt-2">Emails for this experience send from here. Must be a verified sender in your Brevo account. Leave blank to use the global default.</p>
        </div>
      )}

      <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
        <input type="checkbox" checked={w.replay_enabled} onChange={(e) => set({ replay_enabled: e.target.checked })} className="w-4 h-4 accent-[#6D28B5]" data-testid="details-replay" />
        Make replay available to attendees after the session ends
      </label>
    </div>
  );
}

function FileUpload({ slug, kind, value, onChange, accept, testid }) {
  const ref = useRef();
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("webinar_slug", slug || "misc");
    fd.append("kind", kind);
    try {
      const res = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(res.data.url);
      toast.success("Uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
    }
    setUploading(false);
  };
  return (
    <div>
      <input ref={ref} type="file" accept={accept} className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid={testid} />
      <button
        type="button"
        onClick={() => ref.current?.click()}
        disabled={uploading}
        className="border border-[#0A0A0A] hover:bg-[#F3F4F6] px-4 py-2.5 text-sm font-semibold flex items-center gap-2 disabled:opacity-50"
      >
        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
        {value ? "Replace" : "Upload"}
      </button>
    </div>
  );
}

function MediaTab({ w, set }) {
  return (
    <div className="space-y-8 max-w-2xl">
      <div>
        <Field label="Cover image">
          <div className="flex items-start gap-4 mt-1">
            <div className="w-48 h-28 bg-[#F3F4F6] border border-[#E5E7EB] overflow-hidden flex items-center justify-center shrink-0">
              {w.image_url ? <img src={w.image_url} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-6 h-6 text-[#9CA3AF]" />}
            </div>
            <div className="space-y-2 flex-1">
              <FileUpload slug={w.slug} kind="images" value={w.image_url} onChange={(url) => set({ image_url: url })} accept="image/*" testid="upload-image" />
              <input value={w.image_url} onChange={(e) => set({ image_url: e.target.value })} placeholder="or paste an image URL" className={input} data-testid="image-url-input" />
            </div>
          </div>
        </Field>
      </div>

      <div>
        <Field label="Webinar video">
          <div className="flex items-start gap-4 mt-1">
            <div className="w-48 h-28 bg-black border border-[#E5E7EB] overflow-hidden flex items-center justify-center shrink-0">
              {w.video_url ? <video src={w.video_url} className="w-full h-full object-cover" /> : <VideoIcon className="w-6 h-6 text-[#52525B]" />}
            </div>
            <div className="space-y-2 flex-1">
              <StreamVideoUpload title={w.title} value={w.video_url} onChange={(url) => set({ video_url: url })} />
              <input value={w.video_url} onChange={(e) => set({ video_url: e.target.value })} placeholder="or paste an HLS (.m3u8) / MP4 video URL" className={input} data-testid="video-url-input" />
            </div>
          </div>
        </Field>
        <p className="text-xs text-[#9CA3AF] mt-2">
          Videos upload directly to your Bunny.net Stream library (resumable, handles multi-hour files). Playback uses adaptive HLS. You can also paste a direct URL.
        </p>
      </div>
    </div>
  );
}

function ScheduleTab({ w, set }) {
  const [timeInput, setTimeInput] = useState("");
  const addTime = () => {
    if (!/^\d{1,2}:\d{2}$/.test(timeInput)) return toast.error("Use HH:MM format");
    set({ recurring_times: [...(w.recurring_times || []), timeInput].sort() });
    setTimeInput("");
  };
  return (
    <div className="space-y-6 max-w-2xl">
      <Field label="Schedule type">
        <select value={w.schedule_type} onChange={(e) => set({ schedule_type: e.target.value })} className={input} data-testid="schedule-type">
          <option value="just_in_time">Just-in-time (recurring slots every X minutes)</option>
          <option value="recurring">Recurring daily times</option>
          <option value="instant">Watch instantly</option>
        </select>
      </Field>

      {w.schedule_type === "just_in_time" && (
        <Field label="Interval between sessions (minutes)">
          <input type="number" value={w.jit_interval_minutes} onChange={(e) => set({ jit_interval_minutes: parseInt(e.target.value) || 15 })} className={input} data-testid="jit-interval" />
          <p className="text-xs text-[#9CA3AF] mt-2">Attendees see fresh start times like "starts in 7 minutes" — the classic evergreen feel.</p>
        </Field>
      )}

      {w.schedule_type === "recurring" && (
        <Field label="Daily session times">
          <div className="flex gap-2 mb-3">
            <input value={timeInput} onChange={(e) => setTimeInput(e.target.value)} placeholder="14:00" className={input} data-testid="recurring-time-input" />
            <button onClick={addTime} className="bg-[#6D28B5] text-white px-4 font-semibold shrink-0" data-testid="add-time-btn"><Plus className="w-4 h-4" /></button>
          </div>
          <div className="flex flex-wrap gap-2">
            {(w.recurring_times || []).map((t, i) => (
              <span key={i} className="bg-[#F3F4F6] border border-[#E5E7EB] px-3 py-1.5 text-sm font-mono flex items-center gap-2">
                {t}
                <button onClick={() => set({ recurring_times: w.recurring_times.filter((_, j) => j !== i) })}><Trash2 className="w-3.5 h-3.5 text-[#FF3B30]" /></button>
              </span>
            ))}
          </div>
        </Field>
      )}

      {w.schedule_type === "instant" && (
        <p className="text-sm text-[#4B5563] bg-[#F3F4F6] border border-[#E5E7EB] p-4">
          Attendees start watching immediately upon registration (from the beginning of the video).
        </p>
      )}
    </div>
  );
}

function HostsTab({ w, set, me }) {
  const hosts = w.hosts || [];
  const [accounts, setAccounts] = useState([]);
  const [pick, setPick] = useState("");
  const isAdmin = me?.role === "admin";
  useEffect(() => {
    api.get("/admin/hostable-users").then((r) => setAccounts(r.data)).catch(() => setAccounts([]));
  }, []);
  const update = (i, patch) => set({ hosts: hosts.map((h, j) => (j === i ? { ...h, ...patch } : h)) });
  const remove = (i) => set({ hosts: hosts.filter((_, j) => j !== i) });
  const linkedIds = hosts.filter((h) => h.user_id).map((h) => h.user_id);
  const available = accounts.filter((a) => !linkedIds.includes(a.id));
  const acct = (id) => accounts.find((a) => a.id === id);

  const addMyself = () => {
    if (linkedIds.includes(me?.id)) return toast.info("You're already a host on this experience");
    set({ hosts: [...hosts, { id: crypto.randomUUID(), user_id: me.id, title: "", on_screen: true }] });
    toast.success("Added you as a host");
  };
  const linkAccount = () => {
    if (!pick) return;
    set({ hosts: [...hosts, { id: crypto.randomUUID(), user_id: pick, title: "", on_screen: true }] });
    setPick("");
    toast.success("Host linked");
  };

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-4 space-y-3">
        <div className="flex flex-wrap gap-2">
          <button onClick={addMyself} className="bg-[#F1EEFB] text-[#6D28B5] hover:bg-[#E5DBF7] px-4 py-2 text-sm font-semibold flex items-center gap-2" data-testid="add-myself-host-btn">
            <Plus className="w-4 h-4" /> Add me as a host
          </button>
          <button onClick={() => set({ hosts: [...hosts, newHost()] })} className="border border-[#0A0A0A] hover:bg-[#F3F4F6] px-4 py-2 text-sm font-semibold flex items-center gap-2" data-testid="add-guest-host-btn">
            <Plus className="w-4 h-4" /> Add a guest (no account)
          </button>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <select value={pick} onChange={(e) => setPick(e.target.value)} className={input} data-testid="host-account-select">
              <option value="">Link an existing host account…</option>
              {available.map((a) => (
                <option key={a.id} value={a.id}>{a.name} ({a.email}){a.role === "admin" ? " · admin" : ""}</option>
              ))}
            </select>
            <button onClick={linkAccount} disabled={!pick} className="bg-[#6D28B5] text-white hover:bg-[#4A1A7A] disabled:opacity-50 px-4 text-sm font-semibold shrink-0" data-testid="link-host-account-btn">Link</button>
          </div>
        )}
        <p className="text-xs text-[#9CA3AF]">Linked hosts pull their name, photo &amp; bio from their own profile and can manage this experience from their account. Guests are shown publicly but have no login.</p>
      </div>

      {hosts.map((h, i) => {
        const linked = !!h.user_id;
        const a = linked ? acct(h.user_id) : null;
        const dispName = linked ? (a?.name || h.name || "Linked host") : h.name;
        const dispAvatar = linked ? (a?.avatar_url || h.avatar_url) : h.avatar_url;
        return (
          <div key={h.id} className="border border-[#E5E7EB] p-5" data-testid={`host-${i}`}>
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 bg-[#F3F4F6] border border-[#E5E7EB] overflow-hidden flex items-center justify-center shrink-0">
                {dispAvatar ? <img src={dispAvatar} alt="" className="w-full h-full object-cover" /> : <Users className="w-5 h-5 text-[#9CA3AF]" />}
              </div>
              <div className="flex-1 space-y-3">
                {linked ? (
                  <>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold" data-testid={`host-name-${i}`}>{dispName}</span>
                      <span className="text-[10px] uppercase tracking-wide bg-[#F1EEFB] text-[#6D28B5] px-2 py-0.5 rounded-full font-bold">Linked account</span>
                    </div>
                    <p className="text-xs text-[#9CA3AF]">{a?.email || h.linked_email || ""} · Name, photo &amp; bio are managed in their own profile.</p>
                    <input value={h.title || ""} onChange={(e) => update(i, { title: e.target.value })} placeholder="Title / role on this experience (optional)" className={input} data-testid={`host-title-${i}`} />
                  </>
                ) : (
                  <>
                    <div className="grid grid-cols-2 gap-3">
                      <input value={h.name} onChange={(e) => update(i, { name: e.target.value })} placeholder="Name (required)" className={input} data-testid={`host-name-${i}`} />
                      <input value={h.title} onChange={(e) => update(i, { title: e.target.value })} placeholder="Title / role" className={input} />
                    </div>
                    <div className="flex items-center gap-2">
                      <FileUpload slug={w.slug} kind="images" value={h.avatar_url} onChange={(url) => update(i, { avatar_url: url })} accept="image/*" testid={`host-photo-upload-${i}`} />
                      <input value={h.avatar_url} onChange={(e) => update(i, { avatar_url: e.target.value })} placeholder="or paste an image URL" className={input} />
                    </div>
                    <textarea value={h.bio || ""} onChange={(e) => update(i, { bio: e.target.value })} placeholder="Short bio (shown on the reservation page)" rows={2} className={input} data-testid={`host-bio-${i}`} />
                  </>
                )}
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer" data-testid={`host-onscreen-${i}`}>
                    <Monitor className="w-4 h-4" />
                    <input type="checkbox" checked={h.on_screen} onChange={(e) => update(i, { on_screen: e.target.checked })} className="w-4 h-4 accent-[#6D28B5]" />
                    On screen during the experience
                  </label>
                  <button onClick={() => remove(i)} className="text-[#FF3B30] hover:bg-red-50 p-2" data-testid={`remove-host-${i}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function ChatTab({ w, set, reload }) {
  const msgs = w.chat_messages || [];
  const fileRef = useRef();
  const [importing, setImporting] = useState(false);
  const update = (i, patch) => set({ chat_messages: msgs.map((m, j) => (j === i ? { ...m, ...patch } : m)) });
  const importCsv = async (file) => {
    if (!file) return;
    setImporting(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("replace", "true");
    try {
      const res = await api.post(`/admin/webinars/${w.id}/chat-import`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${res.data.imported} messages`);
      reload && reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Import failed");
    }
    setImporting(false);
  };
  return (
    <div className="space-y-3 max-w-2xl">
      <div className="bg-[#F3F4F6] border border-[#E5E7EB] p-4 flex items-start justify-between gap-4">
        <div>
          <div className="font-semibold text-sm flex items-center gap-1.5"><FileUp className="w-4 h-4 text-[#6D28B5]" /> Bulk import from CSV</div>
          <p className="text-xs text-[#6B7280] mt-1">
            Columns: <span className="font-mono">time, name, message</span> (one per row). Time can be <span className="font-mono">mm:ss</span>, <span className="font-mono">h:mm:ss</span>, or seconds. A header row is optional. Importing replaces the current list. No storage needed, it parses instantly.
          </p>
        </div>
        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => { importCsv(e.target.files[0]); e.target.value = ""; }} data-testid="chat-csv-input" />
        <button onClick={() => fileRef.current?.click()} disabled={importing} className="border border-[#0A0A0A] hover:bg-white px-4 py-2.5 text-sm font-semibold flex items-center gap-2 shrink-0 disabled:opacity-50" data-testid="chat-csv-btn">
          {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Import CSV
        </button>
      </div>
      <p className="text-sm text-[#4B5563] mb-2">Scripted messages appear in the live chat at the timestamp you set, making the room feel busy and real.</p>
      {[...msgs].map((m, i) => (
        <div key={m.id} className="border border-[#E5E7EB] p-4 flex items-start gap-3" data-testid={`chat-msg-${i}`}>
          <GripVertical className="w-4 h-4 text-[#D1D5DB] mt-3 shrink-0" />
          <div className="flex-1 space-y-2">
            <div className="grid grid-cols-3 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-[#9CA3AF]">At (mm:ss)</label>
                <input value={secToTime(m.time_seconds)} onChange={(e) => update(i, { time_seconds: timeToSec(e.target.value) })} className={input} data-testid={`chat-time-${i}`} />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-wider font-bold text-[#9CA3AF]">Author</label>
                <input value={m.author} onChange={(e) => update(i, { author: e.target.value })} placeholder="Sarah K." className={input} data-testid={`chat-author-${i}`} />
              </div>
            </div>
            <input value={m.message} onChange={(e) => update(i, { message: e.target.value })} placeholder="Message text…" className={input} data-testid={`chat-text-${i}`} />
          </div>
          <button onClick={() => set({ chat_messages: msgs.filter((_, j) => j !== i) })} className="text-[#FF3B30] hover:bg-red-50 p-2 shrink-0">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
      <button onClick={() => set({ chat_messages: [...msgs, newMsg()] })} className="border border-dashed border-[#9CA3AF] hover:border-[#6D28B5] w-full py-3 text-sm font-semibold flex items-center justify-center gap-2" data-testid="add-chat-btn">
        <Plus className="w-4 h-4" /> Add scripted message
      </button>
    </div>
  );
}

function StreamVideoUpload({ title, value, onChange }) {
  const ref = useRef();
  const [progress, setProgress] = useState(null);
  const start = async (file) => {
    if (!file) return;
    setProgress(0);
    try {
      const { data } = await api.post("/admin/stream/create-video", { title: title || file.name });
      const up = new tus.Upload(file, {
        endpoint: data.tus_endpoint,
        retryDelays: [0, 3000, 5000, 10000, 20000, 60000],
        headers: {
          AuthorizationSignature: data.signature,
          AuthorizationExpire: String(data.expiration),
          VideoId: data.video_id,
          LibraryId: String(data.library_id),
        },
        metadata: { filetype: file.type, title: title || file.name },
        onError: (err) => { toast.error("Upload failed: " + err); setProgress(null); },
        onProgress: (sent, total) => setProgress(Math.round((sent / total) * 100)),
        onSuccess: () => { toast.success("Video uploaded to Bunny Stream"); onChange(data.playback_url); setProgress(null); },
      });
      up.start();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not start upload");
      setProgress(null);
    }
  };
  return (
    <div>
      <input ref={ref} type="file" accept="video/*" className="hidden" onChange={(e) => { start(e.target.files[0]); e.target.value = ""; }} data-testid="upload-video" />
      {progress === null ? (
        <button type="button" onClick={() => ref.current?.click()} className="border border-[#0A0A0A] hover:bg-[#F3F4F6] px-4 py-2.5 text-sm font-semibold flex items-center gap-2">
          <Film className="w-4 h-4" /> {value ? "Replace video" : "Upload video"}
        </button>
      ) : (
        <div className="w-full">
          <div className="flex items-center gap-2 text-sm font-semibold mb-1"><Loader2 className="w-4 h-4 animate-spin" /> Uploading… {progress}%</div>
          <div className="h-2 bg-[#E5E7EB] rounded-full overflow-hidden"><div className="h-full bg-[#6D28B5] transition-all" style={{ width: `${progress}%` }} /></div>
        </div>
      )}
    </div>
  );
}

function timingText(it) {
  if (it.offset_kind === "on_signup") return "Sends immediately when they register";
  if (it.offset_kind === "fixed") return `Fixed time: ${it.fixed_datetime || "(set a date)"}`;
  const m = Math.abs(it.offset_minutes || 0);
  let human;
  if (m !== 0 && m % 43200 === 0) human = `${m / 43200} month(s)`;
  else if (m !== 0 && m % 1440 === 0) human = `${m / 1440} day(s)`;
  else if (m !== 0 && m % 60 === 0) human = `${m / 60} hour(s)`;
  else human = `${m} min`;
  if (it.offset_kind === "after_end") return `Sends ${human} after it ends`;
  return (it.offset_minutes || 0) < 0 ? `Sends ${human} before the session` : `Sends ${human} after the session start`;
}

const OFFSET_UNITS = [
  { key: "minutes", label: "minutes", mins: 1 },
  { key: "hours", label: "hours", mins: 60 },
  { key: "days", label: "days", mins: 1440 },
  { key: "months", label: "months", mins: 43200 },
];

function splitOffset(totalMinutes) {
  const m = Math.abs(totalMinutes || 0);
  for (const u of [...OFFSET_UNITS].reverse()) {
    if (m !== 0 && m % u.mins === 0) return { value: m / u.mins, unit: u.key };
  }
  return { value: m, unit: "minutes" };
}

function OffsetInput({ minutes, onChange, allowNegative = false, testidPrefix = "offset" }) {
  const init = splitOffset(minutes);
  const [value, setValue] = useState(init.value);
  const [unit, setUnit] = useState(init.unit);
  const [before, setBefore] = useState((minutes || 0) < 0);
  const emit = (v, u, bef) => {
    const mins = OFFSET_UNITS.find((x) => x.key === u).mins;
    let total = (Number(v) || 0) * mins;
    if (allowNegative && bef) total = -total;
    onChange(total);
  };
  return (
    <div className="flex items-end gap-2">
      {allowNegative && (
        <div className="flex-1">
          <label className="text-[10px] uppercase tracking-wider font-bold text-[#9CA3AF]">When</label>
          <select value={before ? "before" : "after"} onChange={(e) => { const bef = e.target.value === "before"; setBefore(bef); emit(value, unit, bef); }} className={input} data-testid={`${testidPrefix}-direction`}>
            <option value="after">After</option>
            <option value="before">Before</option>
          </select>
        </div>
      )}
      <div className="flex-1">
        <label className="text-[10px] uppercase tracking-wider font-bold text-[#9CA3AF]">Amount</label>
        <input type="number" min="0" value={value} onChange={(e) => { setValue(e.target.value); emit(e.target.value, unit, before); }} className={input} data-testid={`${testidPrefix}-value`} />
      </div>
      <div className="flex-1">
        <label className="text-[10px] uppercase tracking-wider font-bold text-[#9CA3AF]">Unit</label>
        <select value={unit} onChange={(e) => { setUnit(e.target.value); emit(value, e.target.value, before); }} className={input} data-testid={`${testidPrefix}-unit`}>
          {OFFSET_UNITS.map((u) => <option key={u.key} value={u.key}>{u.label}</option>)}
        </select>
      </div>
    </div>
  );
}

function SequenceTab({ w }) {
  const [items, setItems] = useState(null);
  const [gen, setGen] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const mounted = useRef(true);
  useEffect(() => () => { mounted.current = false; }, []);
  const load = () => api.get(`/admin/webinars/${w.id}/sequence`).then((r) => setItems(r.data)).catch(() => setItems([]));
  const startPolling = (startedAt) => {
    const poll = async () => {
      if (!mounted.current) return;
      try {
        const jr = await api.get(`/admin/webinars/${w.id}/sequence/job`);
        const st = jr.data?.status;
        if (st === "done") {
          await load();
          if (mounted.current) { toast.success("AI drafted your email sequence"); setGen(false); }
          return;
        }
        if (st === "error") {
          if (mounted.current) { toast.error("Generation failed. Please try again."); setGen(false); }
          return;
        }
        if (Date.now() - startedAt > 420000) {
          if (mounted.current) { toast.error("This is taking longer than usual. It keeps working in the background — check back in a moment."); setGen(false); }
          return;
        }
      } catch (e) { /* keep polling */ }
      setTimeout(poll, 4000);
    };
    setTimeout(poll, 4000);
  };
  useEffect(() => {
    load();
    // Resume the generating state if a job is already running for this experience
    api.get(`/admin/webinars/${w.id}/sequence/job`).then((r) => {
      if (r.data?.status === "running" && mounted.current) { setGen(true); startPolling(Date.now()); }
    }).catch(() => {});
    /* eslint-disable-next-line */
  }, [w.id]);
  const generate = async () => {
    setGen(true);
    try {
      await api.post(`/admin/webinars/${w.id}/sequence/generate`, { origin: window.location.origin });
      toast.info("AI is drafting your 5 emails. This usually takes 1 to 3 minutes — you can keep working and they'll appear here when ready.");
      startPolling(Date.now());
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Generation failed");
      setGen(false);
    }
  };
  return (
    <div className="space-y-4 max-w-3xl">
      <div className="bg-[#F3F4F6] border border-[#E5E7EB] p-5 flex items-start justify-between gap-4">
        <div>
          <div className="font-semibold flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#6D28B5]" /> One-click email sequence</div>
          <p className="text-xs text-[#6B7280] mt-1 max-w-md">AI writes a confirmation, two reminders, and two follow-ups (tied to this experience and its CTA) all at once. Edit and schedule each below. Reminders/follow-ups send automatically relative to each attendee's chosen session time.</p>
          {gen && <p className="text-xs text-[#6D28B5] font-semibold mt-2" data-testid="sequence-generating-notice">Generating… this usually takes 1 to 3 minutes. You can navigate away — the emails will appear when ready.</p>}
        </div>
        <button onClick={generate} disabled={gen} className="bg-[#6D28B5] text-white hover:bg-[#4A1A7A] disabled:opacity-50 px-4 py-2.5 font-semibold flex items-center gap-2 shrink-0" data-testid="generate-sequence-btn">
          {gen ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} {gen ? "Generating…" : (items && items.length ? "Regenerate all" : "Generate all")}
        </button>
      </div>
      {items === null ? (
        <div className="text-[#9CA3AF]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-[#9CA3AF] p-10 text-center text-[#6B7280]">No sequence yet. Click “Generate all” to let AI draft it, or add your own email below.</div>
      ) : (
        items.map((it) => <SequenceItemCard key={it.id} item={it} onSaved={load} />)
      )}
      <button onClick={() => setShowAdd(true)} className="border border-dashed border-[#9CA3AF] hover:border-[#6D28B5] w-full py-3 text-sm font-semibold flex items-center justify-center gap-2" data-testid="add-email-btn">
        <Plus className="w-4 h-4" /> Add another email
      </button>
      {showAdd && <AddEmailModal webinarId={w.id} onClose={() => setShowAdd(false)} onAdded={() => { setShowAdd(false); load(); }} />}
    </div>
  );
}

function AddEmailModal({ webinarId, onClose, onAdded }) {
  const [mode, setMode] = useState("ai"); // ai | manual
  const [f, setF] = useState({ label: "", prompt: "", subject: "", html_content: "", offset_kind: "after_end", offset_minutes: 2880 });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    setSaving(true);
    try {
      const body = mode === "ai"
        ? { label: f.label || "Custom email", prompt: f.prompt, offset_kind: f.offset_kind, offset_minutes: Number(f.offset_minutes) || 0 }
        : { label: f.label || "Custom email", subject: f.subject, html_content: f.html_content, offset_kind: f.offset_kind, offset_minutes: Number(f.offset_minutes) || 0 };
      await api.post(`/admin/webinars/${webinarId}/sequence/item`, body);
      toast.success("Email added to your sequence");
      onAdded();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to add email");
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="add-email-modal">
      <div className="bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E7EB]">
          <h2 className="font-heading font-black text-xl tracking-tighter">Add an email</h2>
          <button onClick={onClose} className="p-2 hover:bg-[#F3F4F6]"><Trash2 className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex gap-2">
            <button onClick={() => setMode("ai")} className={`flex-1 px-3 py-2 text-sm font-semibold border ${mode === "ai" ? "bg-[#6D28B5] text-white border-[#6D28B5]" : "border-[#0A0A0A]"}`} data-testid="add-email-mode-ai">Let AI write it</button>
            <button onClick={() => setMode("manual")} className={`flex-1 px-3 py-2 text-sm font-semibold border ${mode === "manual" ? "bg-[#6D28B5] text-white border-[#6D28B5]" : "border-[#0A0A0A]"}`} data-testid="add-email-mode-manual">Write it myself</button>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[#9CA3AF]">Label</label>
            <input value={f.label} onChange={(e) => set("label", e.target.value)} placeholder="e.g. Bonus offer, 3 days after" className={input} data-testid="add-email-label" />
          </div>
          {mode === "ai" ? (
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#9CA3AF]">What should this email say?</label>
              <textarea value={f.prompt} onChange={(e) => set("prompt", e.target.value)} rows={3} placeholder="A warm note sharing a special bonus discount that expires in 48 hours." className={input} data-testid="add-email-prompt" />
            </div>
          ) : (
            <>
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-[#9CA3AF]">Subject</label>
                <input value={f.subject} onChange={(e) => set("subject", e.target.value)} className={input} data-testid="add-email-subject" />
              </div>
              <HtmlEditor value={f.html_content} onChange={(v) => set("html_content", v)} rows={6} testid="add-email-html" />
            </>
          )}
          <div className="grid grid-cols-1 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#9CA3AF]">Timing</label>
              <select value={f.offset_kind} onChange={(e) => set("offset_kind", e.target.value)} className={input}>
                <option value="after_end">After it ends</option>
                <option value="relative">Relative to session start</option>
                <option value="on_signup">Right after signup</option>
              </select>
            </div>
            {f.offset_kind !== "on_signup" && (
              <OffsetInput
                key={f.offset_kind}
                minutes={Number(f.offset_minutes) || 0}
                allowNegative={f.offset_kind === "relative"}
                onChange={(v) => set("offset_minutes", v)}
                testidPrefix="add-email-offset"
              />
            )}
          </div>
          <button onClick={submit} disabled={saving || (mode === "ai" && !f.prompt)} className="w-full bg-[#6D28B5] text-white hover:bg-[#4A1A7A] disabled:opacity-50 px-6 py-3 font-semibold flex items-center justify-center gap-2" data-testid="add-email-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add to sequence
          </button>
        </div>
      </div>
    </div>
  );
}

function SequenceItemCard({ item, onSaved }) {
  const [it, setIt] = useState(item);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const set = (patch) => setIt((p) => ({ ...p, ...patch }));
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/sequence/${it.id}`, {
        subject: it.subject, html_content: it.html_content, enabled: it.enabled,
        offset_kind: it.offset_kind, offset_minutes: Number(it.offset_minutes) || 0, fixed_datetime: it.fixed_datetime || "", label: it.label,
      });
      toast.success("Saved");
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Save failed");
    }
    setSaving(false);
  };
  const remove = async () => {
    if (!window.confirm("Delete this email from the sequence?")) return;
    try {
      await api.delete(`/admin/sequence/${it.id}`);
      toast.success("Email removed");
      onSaved && onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    }
  };
  const toggleEnabled = async (checked) => {
    set({ enabled: checked });
    try {
      await api.put(`/admin/sequence/${it.id}`, {
        subject: it.subject, html_content: it.html_content, enabled: checked,
        offset_kind: it.offset_kind, offset_minutes: Number(it.offset_minutes) || 0, fixed_datetime: it.fixed_datetime || "", label: it.label,
      });
      toast.success(checked ? "Email activated" : "Email paused");
      onSaved && onSaved();
    } catch (err) {
      set({ enabled: !checked });
      toast.error("Couldn't update. Please try again.");
    }
  };
  return (
    <div className="border border-[#E5E7EB] bg-white" data-testid={`sequence-item-${it.type}`}>
      <div className="flex items-center gap-3 p-4">
        <label className="flex items-center gap-2 cursor-pointer shrink-0" title={it.enabled ? "Active — this email will be sent" : "Paused — this email will not be sent"}>
          <input type="checkbox" checked={!!it.enabled} onChange={(e) => toggleEnabled(e.target.checked)} className="w-4 h-4 accent-[#6D28B5]" data-testid={`sequence-enabled-${it.type}`} />
          <span className={`text-xs font-semibold ${it.enabled ? "text-[#16A34A]" : "text-[#9CA3AF]"}`}>{it.enabled ? "Active" : "Paused"}</span>
        </label>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm">{it.label}</div>
          <div className="text-xs text-[#6B7280] flex items-center gap-1"><Clock className="w-3 h-3" /> {timingText(it)}</div>
        </div>
        <button onClick={() => setOpen(!open)} className="border border-[#0A0A0A] hover:bg-[#F3F4F6] px-3 py-1.5 text-sm font-semibold">{open ? "Close" : "Edit"}</button>
        <button onClick={remove} className="text-[#FF3B30] hover:bg-red-50 p-2" data-testid={`delete-sequence-${it.id}`}><Trash2 className="w-4 h-4" /></button>
      </div>
      {open && (
        <div className="border-t border-[#E5E7EB] p-4 space-y-3">
          {it.offset_kind !== "on_signup" && (
            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-wider font-bold text-[#9CA3AF]">Timing</label>
                <select value={it.offset_kind} onChange={(e) => set({ offset_kind: e.target.value })} className={input}>
                  <option value="relative">Relative to session start</option>
                  <option value="after_end">After it ends</option>
                  <option value="fixed">Fixed date/time</option>
                </select>
              </div>
              {it.offset_kind === "fixed" ? (
                <div>
                  <label className="text-[10px] uppercase tracking-wider font-bold text-[#9CA3AF]">Date & time</label>
                  <input type="datetime-local" value={(it.fixed_datetime || "").slice(0, 16)} onChange={(e) => set({ fixed_datetime: e.target.value })} className={input} />
                </div>
              ) : (
                <OffsetInput
                  key={it.offset_kind}
                  minutes={Number(it.offset_minutes) || 0}
                  allowNegative={it.offset_kind === "relative"}
                  onChange={(v) => set({ offset_minutes: v })}
                  testidPrefix="edit-offset"
                />
              )}
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-[#9CA3AF]">Subject</label>
            <input value={it.subject || ""} onChange={(e) => set({ subject: e.target.value })} className={input} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <HtmlEditor value={it.html_content || ""} onChange={(v) => set({ html_content: v })} rows={8} testid={`edit-html-${it.id}`} label="HTML" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#9CA3AF]">Preview</label>
              <iframe title="seq-preview" srcDoc={it.html_content} className="w-full h-[200px] bg-white border border-[#E5E7EB]" />
            </div>
          </div>
          <button onClick={save} disabled={saving} className="bg-[#6D28B5] text-white hover:bg-[#4A1A7A] disabled:opacity-50 px-5 py-2 font-semibold flex items-center gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save email
          </button>
        </div>
      )}
    </div>
  );
}

function StarRow({ value }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`w-3.5 h-3.5 ${n <= value ? "fill-[#F2B705] text-[#F2B705]" : "text-[#D9D3EC]"}`} />)}
    </div>
  );
}

function ReviewsTab({ w, isAdmin }) {
  const [reviews, setReviews] = useState(null);
  const [gen, setGen] = useState(false);
  const load = () => api.get(`/admin/webinars/${w.id}/reviews`).then((r) => setReviews(r.data)).catch(() => setReviews([]));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [w.id]);
  const generate = async () => {
    setGen(true);
    try { await api.post(`/admin/webinars/${w.id}/reviews/generate`); toast.success("AI review added"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed to generate"); }
    setGen(false);
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this review?")) return;
    try { await api.delete(`/admin/reviews/${id}`); toast.success("Review deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };
  const toggleHide = async (rv) => {
    try { await api.post(`/admin/reviews/${rv.id}/hide`, { hidden: !rv.hidden }); toast.success(rv.hidden ? "Review restored" : "Review hidden"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };
  const save = async (id, patch) => {
    try { await api.patch(`/admin/reviews/${id}`, patch); toast.success("Review updated"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };
  return (
    <div className="space-y-4 max-w-3xl">
      <div className="bg-[#F3F4F6] border border-[#E5E7EB] p-5 flex items-start justify-between gap-4">
        <div>
          <div className="font-semibold flex items-center gap-2"><Star className="w-4 h-4 text-[#6D28B5]" /> Experience reviews</div>
          <p className="text-xs text-[#6B7280] mt-1 max-w-md">Attendees who have completed this experience can leave a review on its page.{isAdmin ? " You can also seed a believable review with AI to get things started, then edit it however you like." : ""}</p>
        </div>
        {isAdmin && (
          <button onClick={generate} disabled={gen} className="bg-[#6D28B5] text-white hover:bg-[#4A1A7A] disabled:opacity-50 px-4 py-2.5 font-semibold flex items-center gap-2 shrink-0" data-testid="generate-review-btn">
            {gen ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generate AI review
          </button>
        )}
      </div>
      {reviews === null ? (
        <div className="text-[#9CA3AF]">Loading…</div>
      ) : reviews.length === 0 ? (
        <div className="border border-dashed border-[#9CA3AF] p-10 text-center text-[#6B7280]">No reviews yet.</div>
      ) : (
        reviews.map((rv) => (
          <ReviewRow key={rv.id} rv={rv} onToggleHide={toggleHide} onRemove={remove} onSave={save} />
        ))
      )}
    </div>
  );
}

function ReviewRow({ rv, onToggleHide, onRemove, onSave }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(rv.author_name);
  const [rating, setRating] = useState(rv.rating);
  const [body, setBody] = useState(rv.body);
  const [saving, setSaving] = useState(false);
  const start = () => { setName(rv.author_name); setRating(rv.rating); setBody(rv.body); setEditing(true); };
  const submit = async () => {
    if (!name.trim() || !body.trim()) return;
    setSaving(true);
    await onSave(rv.id, { author_name: name.trim(), rating, body: body.trim() });
    setSaving(false); setEditing(false);
  };
  if (editing) {
    return (
      <div className="border border-[#6D28B5] bg-white p-4 space-y-3" data-testid={`review-row-${rv.id}`}>
        <div className="flex items-center gap-3 flex-wrap">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Reviewer name" data-testid={`edit-review-name-${rv.id}`}
            className="border border-[#E5E7EB] px-3 py-1.5 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[#6D28B5]" />
          <div className="flex items-center gap-1" data-testid={`edit-review-rating-${rv.id}`}>
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" onClick={() => setRating(n)} className="p-0.5" data-testid={`edit-review-star-${rv.id}-${n}`}>
                <Star className={`w-5 h-5 ${n <= rating ? "fill-[#F2B705] text-[#F2B705]" : "text-[#D9D3EC]"}`} />
              </button>
            ))}
          </div>
        </div>
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} placeholder="Review text" data-testid={`edit-review-body-${rv.id}`}
          className="w-full border border-[#E5E7EB] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#6D28B5]" />
        <div className="flex items-center gap-2">
          <button onClick={submit} disabled={saving || !name.trim() || !body.trim()} className="bg-[#6D28B5] text-white hover:bg-[#4A1A7A] disabled:opacity-50 px-4 py-2 text-sm font-semibold flex items-center gap-1.5" data-testid={`save-review-${rv.id}`}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save
          </button>
          <button onClick={() => setEditing(false)} className="border border-[#E5E7EB] hover:bg-[#F3F4F6] px-4 py-2 text-sm font-semibold flex items-center gap-1.5" data-testid={`cancel-review-${rv.id}`}>
            <X className="w-4 h-4" /> Cancel
          </button>
        </div>
      </div>
    );
  }
  return (
    <div className={`border border-[#E5E7EB] bg-white p-4 flex items-start justify-between gap-3 ${rv.hidden ? "opacity-50" : ""}`} data-testid={`review-row-${rv.id}`}>
      <div>
        <div className="flex items-center gap-2">
          <span className="font-semibold text-sm">{rv.author_name}</span>
          {rv.hidden && <span className="text-[10px] uppercase tracking-wider bg-[#FEE2E2] text-[#B91C1C] px-1.5 py-0.5">Hidden</span>}
        </div>
        <div className="mt-1"><StarRow value={rv.rating} /></div>
        <p className="text-sm text-[#374151] mt-1.5">{rv.body}</p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button onClick={start} className="text-[#6D28B5] hover:bg-purple-50 p-2" data-testid={`edit-review-${rv.id}`} title="Edit"><Pencil className="w-4 h-4" /></button>
        <button onClick={() => onToggleHide(rv)} className="text-[#B45309] hover:bg-amber-50 p-2" data-testid={`hide-review-${rv.id}`} title={rv.hidden ? "Restore" : "Hide"}>
          {rv.hidden ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
        </button>
        <button onClick={() => onRemove(rv.id)} className="text-[#FF3B30] hover:bg-red-50 p-2" data-testid={`delete-review-${rv.id}`} title="Delete"><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
  );
}

function EndScreenTab({ w, set }) {
  const es = w.end_screen || {};
  const update = (patch) => set({ end_screen: { ...es, ...patch } });
  return (
    <div className="space-y-6 max-w-2xl">
      <p className="text-sm text-[#4B5563]">Control what attendees see when the experience ends, a custom closing screen with an image, message, and a link.</p>
      <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
        <input type="checkbox" checked={!!es.enabled} onChange={(e) => update({ enabled: e.target.checked })} className="w-4 h-4 accent-[#6D28B5]" data-testid="endscreen-enabled" />
        Show a custom end screen
      </label>

      {es.enabled && (
        <>
          <Field label="End screen image">
            <div className="flex items-start gap-4 mt-1">
              <div className="w-48 h-28 bg-[#F3F4F6] border border-[#E5E7EB] overflow-hidden flex items-center justify-center shrink-0">
                {es.image_url ? <img src={es.image_url} alt="" className="w-full h-full object-cover" /> : <ImageIcon className="w-6 h-6 text-[#9CA3AF]" />}
              </div>
              <div className="space-y-2 flex-1">
                <FileUpload slug={w.slug} kind="images" value={es.image_url} onChange={(url) => update({ image_url: url })} accept="image/*" testid="endscreen-upload-image" />
                <input value={es.image_url || ""} onChange={(e) => update({ image_url: e.target.value })} placeholder="or paste an image URL" className={input} />
              </div>
            </div>
          </Field>
          <Field label="Headline">
            <input value={es.headline || ""} onChange={(e) => update({ headline: e.target.value })} className={input} data-testid="endscreen-headline" />
          </Field>
          <Field label="Message">
            <textarea value={es.body || ""} onChange={(e) => update({ body: e.target.value })} rows={3} className={input} data-testid="endscreen-body" />
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Button text">
              <input value={es.button_text || ""} onChange={(e) => update({ button_text: e.target.value })} placeholder="Join the Inner Circle" className={input} />
            </Field>
            <Field label="Button link">
              <input value={es.button_url || ""} onChange={(e) => update({ button_url: e.target.value })} placeholder="https://..." className={input} data-testid="endscreen-url" />
            </Field>
          </div>
        </>
      )}
    </div>
  );
}

function CtasTab({ w, set }) {
  const ctas = w.ctas || [];
  const update = (i, patch) => set({ ctas: ctas.map((c, j) => (j === i ? { ...c, ...patch } : c)) });
  return (
    <div className="space-y-3 max-w-2xl">
      <p className="text-sm text-[#4B5563] mb-2">Offers that pop up over the video at the perfect moment. Set duration to 0 to keep it on screen until the end.</p>
      {ctas.map((c, i) => (
        <div key={c.id} className="border border-[#E5E7EB] p-4 space-y-3" data-testid={`cta-${i}`}>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#9CA3AF]">Show at (mm:ss)</label>
              <input value={secToTime(c.time_seconds)} onChange={(e) => update(i, { time_seconds: timeToSec(e.target.value) })} className={input} data-testid={`cta-time-${i}`} />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-wider font-bold text-[#9CA3AF]">Duration sec (0 = until end)</label>
              <input type="number" value={c.duration_seconds} onChange={(e) => update(i, { duration_seconds: parseInt(e.target.value) || 0 })} className={input} />
            </div>
          </div>
          <input value={c.headline} onChange={(e) => update(i, { headline: e.target.value })} placeholder="Headline" className={input} data-testid={`cta-headline-${i}`} />
          <input value={c.description} onChange={(e) => update(i, { description: e.target.value })} placeholder="Short description" className={input} />
          <div className="grid grid-cols-2 gap-3">
            <input value={c.button_text} onChange={(e) => update(i, { button_text: e.target.value })} placeholder="Button text" className={input} />
            <input value={c.button_url} onChange={(e) => update(i, { button_url: e.target.value })} placeholder="https://buy-link.com" className={input} data-testid={`cta-url-${i}`} />
          </div>
          <button onClick={() => set({ ctas: ctas.filter((_, j) => j !== i) })} className="text-[#FF3B30] hover:bg-red-50 px-3 py-1.5 text-sm font-semibold flex items-center gap-1.5">
            <Trash2 className="w-4 h-4" /> Remove
          </button>
        </div>
      ))}
      <button onClick={() => set({ ctas: [...ctas, newCta()] })} className="border border-dashed border-[#9CA3AF] hover:border-[#6D28B5] w-full py-3 text-sm font-semibold flex items-center justify-center gap-2" data-testid="add-cta-btn">
        <Plus className="w-4 h-4" /> Add timed CTA
      </button>
    </div>
  );
}
